/**
 * Cosmos Universities directory — published at /universities.
 *
 * One-screen browse of every university and lab on the platform, mirroring
 * the Canva mockup:
 *   1. Hero pill + headline + dynamic "{N} universities and labs across
 *      {M} countries" subtitle (numbers come from /public/analytics_pie.json)
 *   2. Region filter pills (All / Asia / Oceania / Europe / North America)
 *   3. Responsive grid of cards — flag, university name, tech count, top 3
 *      major-category tags. Top-3 categories are derived from
 *      `industry_categories.by_university[<name>].major_categories` in the
 *      same analytics_pie snapshot the homepage already serves.
 *
 * Same chrome (NewNavBar + NewFooter + auth-modal stack + WhitePaperModal)
 * as the homepage / About Us / R&D Symposium, so cross-page navigation and
 * login work identically here. Padding matches the rest of the site
 * (`px-4 sm:px-6 lg:px-8`).
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { NewNavBar, NavItem, NewFooter, COUNTRY_ISO, flagSrc } from './NewHome';
import { PrivacyPolicyModal } from './PrivacyPolicyModal';
import { TermsOfServiceModal } from './TermsOfServiceModal';
import { WhitePaperModal } from './WhitePaperModal';
import LoginModal from './LoginModal';
import SignUpModal from './SignUpModal';
import { SignupRequiredModal } from '../../components/SignupRequiredModal';
import { trackClick } from '../../utils/analytics';
import { apiService } from '../../services/apiService';
// "Get your university listed" banner + request form (its own scoped styles).
import { ListingRequestBanner } from './techpages/ListingRequest';

// ---------------------------------------------------------------------------
// Region mapping — keyed on the ISO-2 country code we already use for flags
// ---------------------------------------------------------------------------

type RegionKey = 'ALL' | 'AS' | 'OC' | 'EU' | 'NA';

const REGIONS: ReadonlyArray<{ key: RegionKey; label: string; eventTag: string }> = [
  { key: 'ALL', label: 'All regions',   eventTag: 'all' },
  { key: 'AS',  label: 'Asia',          eventTag: 'asia' },
  { key: 'OC',  label: 'Oceania',       eventTag: 'oceania' },
  { key: 'EU',  label: 'Europe',        eventTag: 'europe' },
  { key: 'NA',  label: 'North America', eventTag: 'north_america' },
];

/** ISO-2 → region. Covers every country currently in COUNTRY_ISO plus a few
 *  obvious neighbours so new entries don't fall into "ALL" silently. */
const REGION_BY_ISO: Record<string, RegionKey> = {
  // Asia
  in: 'AS', cn: 'AS', jp: 'AS', kr: 'AS', sg: 'AS', il: 'AS',
  tw: 'AS', hk: 'AS', th: 'AS', my: 'AS', id: 'AS', ph: 'AS',
  vn: 'AS', sa: 'AS', ae: 'AS', tr: 'AS', pk: 'AS', bd: 'AS',
  // Oceania
  au: 'OC', nz: 'OC',
  // Europe
  gb: 'EU', de: 'EU', fr: 'EU', it: 'EU', es: 'EU', nl: 'EU',
  ch: 'EU', se: 'EU', be: 'EU', dk: 'EU', no: 'EU', fi: 'EU',
  at: 'EU', ie: 'EU', pl: 'EU', pt: 'EU', cz: 'EU', gr: 'EU',
  // North America
  us: 'NA', ca: 'NA', mx: 'NA',
};

const regionOf = (countryName: string): RegionKey | null => {
  const iso = COUNTRY_ISO[countryName];
  if (!iso) return null;
  return REGION_BY_ISO[iso] || null;
};

// ---------------------------------------------------------------------------
// Compact display names for the long category strings on the chip tags
// ---------------------------------------------------------------------------

const CATEGORY_SHORT: Record<string, string> = {
  'Healthcare & Life Sciences':           'Healthcare',
  'ICT, AI & Computing':                  'AI & Tech',
  'Advanced Materials & Manufacturing':   'Materials',
  'Agriculture & Food':                   'Agri & Food',
  'Energy & Sustainability':              'Energy',
  'Science, Exploration & Space':         'Science',
  'Consumer Goods & Services':            'Consumer',
  'Electronics & Photonics':              'Electronics',
  'Electronics & Photonic':               'Electronics',
  'Transportation & Mobility':            'Mobility',
  'Defense & Security':                   'Defense',
  'Financial Services':                   'FinServ',
  'Education':                            'EdTech',
  'Construction & Infrastructure':        'Infra',
};

const shortenCategory = (raw: string): string => CATEGORY_SHORT[raw] || raw;

// ---------------------------------------------------------------------------
// /public/analytics_pie.json schema we depend on
// ---------------------------------------------------------------------------

interface UniRow {
  name: string;
  country: string;
  technologies: number;
  researchers?: number;
  /** From the live directory API (absent in the static snapshot): the UTECH
   *  code, the website label and the university's public page. */
  code?: string;
  label?: string | null;
  page_path?: string | null;
}

interface AnalyticsPiePayload {
  metadata?: {
    total_technologies?: number;
    total_universities?: number;
  };
  universities?: UniRow[];
  country_distribution?: Record<string, { universities: number; technologies: number; researchers: number }>;
  industry_categories?: {
    global?: {
      major_categories?: Record<string, number>;
      subcategories?: Record<string, number>;
    };
    by_university?: Record<string, {
      major_categories?: Record<string, number>;
      subcategories?: Record<string, number>;
    }>;
  };
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

interface UniversitiesPageProps {
  onLogin: (email: string) => void;
}

const UniversitiesPage: React.FC<UniversitiesPageProps> = ({ onLogin }) => {
  const navigate = useNavigate();
  // ---- Auth modal stack (same as About Us / R&D Symposium) ----------------
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showSignUpModal, setShowSignUpModal] = useState(false);
  const [showSignupRequiredModal, setShowSignupRequiredModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showWhitePaperModal, setShowWhitePaperModal] = useState(false);

  const closeAllAuthModals = () => {
    setShowLoginModal(false);
    setShowSignUpModal(false);
    setShowSignupRequiredModal(false);
  };

  const openLogin = () => { trackClick('universities_open_login'); closeAllAuthModals(); setShowLoginModal(true); };
  const openSignUp = () => { trackClick('universities_open_signup'); closeAllAuthModals(); setShowSignUpModal(true); };
  const handleSignUpSuccess = () => { setShowSignUpModal(false); setShowLoginModal(true); };

  const handleLoginClick = () => { trackClick('universities_nav_login_signup'); openLogin(); };
  const handleAboutUs = () => { trackClick('universities_nav_about_us'); window.location.href = '/about'; };
  const handleLogoClick = () => { trackClick('universities_nav_logo'); window.location.href = '/'; };

  const navItems: NavItem[] = [
    { id: 'about_us', label: 'About Us', href: '#', onClick: handleAboutUs },
  ];

  // ---- Data load ----------------------------------------------------------
  const [data, setData] = useState<AnalyticsPiePayload | null>(null);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    trackClick('universities_page_view');
    let cancelled = false;
    (async () => {
      try {
        // Live from the corpus first. The bundled snapshot drifts — it
        // advertised 36 universities while the corpus held 37 — so it is now
        // only the last resort, for when the API is unreachable.
        let json: AnalyticsPiePayload | null = null;
        let source = 'api';
        try {
          const res = await apiService.universitiesDirectory();
          const d: any = res.data;
          // An empty list means the server fell all the way through its own
          // fallbacks; treat that as a miss rather than rendering nothing.
          if (res.success && d?.success && (d.universities?.length || 0) > 0) {
            json = d as AnalyticsPiePayload;
            source = d?.metadata?.served_from || 'api';
          }
        } catch {
          /* fall through to the static snapshot */
        }
        if (!json) {
          source = 'static';
          // `no-cache` so a freshly-published analytics snapshot (new
          // universities, updated counts) shows up on the next page load
          // instead of being pinned to an old browser cache.
          const r = await fetch('/analytics_pie.json', { cache: 'no-cache' });
          if (!r.ok) throw new Error(`status ${r.status}`);
          json = (await r.json()) as AnalyticsPiePayload;
        }
        if (!cancelled) {
          setData(json);
          trackClick('universities_data_loaded', {
            source,
            university_count: json?.universities?.length || 0,
            country_count: json?.country_distribution ? Object.keys(json.country_distribution).length : 0,
          });
        }
      } catch (err: any) {
        if (!cancelled) {
          setLoadError(true);
          trackClick('universities_data_load_error', { error: err?.message || 'unknown' });
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // ---- Region filter ------------------------------------------------------
  const [region, setRegion] = useState<RegionKey>('ALL');

  const universities = useMemo<UniRow[]>(() => data?.universities || [], [data]);

  const filtered = useMemo(() => {
    if (region === 'ALL') return universities;
    return universities.filter(u => regionOf(u.country) === region);
  }, [universities, region]);

  // Fire an "empty filter" drop-off signal so analytics can see dead-end
  // region picks (e.g. someone filters to Africa and finds nothing). Only
  // fires once per (region, data-loaded) combination — the dependency
  // array ensures we don't spam events.
  useEffect(() => {
    if (!data) return;
    if (region === 'ALL') return;
    if (filtered.length === 0) {
      trackClick('universities_empty_region', { region });
    }
  }, [data, region, filtered.length]);

  // ---- Top-3 categories per university ------------------------------------
  const topCategoriesOf = (uniName: string): string[] => {
    const bu = data?.industry_categories?.by_university?.[uniName];
    if (!bu?.major_categories) return [];
    return Object.entries(bu.major_categories)
      .filter(([, count]) => typeof count === 'number' && count > 0)
      .sort((a, b) => (b[1] as number) - (a[1] as number))
      .slice(0, 3)
      .map(([catName]) => catName);
  };

  // ---- Header counts ------------------------------------------------------
  const totalUniversities = data?.metadata?.total_universities ?? universities.length;
  const totalCountries = data?.country_distribution ? Object.keys(data.country_distribution).length : 0;

  return (
    <div className="bg-white min-h-screen font-dm-sans text-grayscale-1200 overflow-x-hidden">
      <NewNavBar
        navItems={navItems}
        onLoginClick={handleLoginClick}
        onLogoClick={handleLogoClick}
      />

      <main className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* ====================== HERO ============================== */}
        <section className="text-center pt-8 sm:pt-12 pb-6 sm:pb-8">
          <div className="inline-flex items-center px-4 py-1.5 rounded-full bg-secondary-100 border border-secondary-400 text-secondary-og-600 text-xs sm:text-sm font-bold tracking-wide">
            UNIVERSITY
          </div>
          <h1 className="mt-5 sm:mt-7 text-3xl sm:text-4xl lg:text-5xl font-bold text-grayscale-1300 leading-tight px-3 max-w-4xl mx-auto">
            Tech offers from the world&apos;s leading research institutions
          </h1>
          <p className="mt-4 sm:mt-5 text-sm sm:text-base text-grayscale-1100 max-w-2xl mx-auto px-3">
            {totalUniversities > 0 && totalCountries > 0 ? (
              <>
                {totalUniversities} universities and labs across {totalCountries} countries — all on one platform
              </>
            ) : (
              <>Universities and labs across multiple countries — all on one platform</>
            )}
          </p>

          {/* Region filter pills */}
          <div
            className="mt-6 sm:mt-8 flex flex-wrap items-center justify-center gap-2 sm:gap-3"
            role="tablist"
            aria-label="Filter universities by region"
          >
            {REGIONS.map(r => {
              const selected = region === r.key;
              return (
                <button
                  key={r.key}
                  role="tab"
                  aria-selected={selected}
                  onClick={() => {
                    setRegion(r.key);
                    trackClick('universities_region_filter', { region: r.eventTag });
                  }}
                  className={
                    selected
                      ? 'px-4 sm:px-5 py-1.5 sm:py-2 rounded-full border border-primary-600 bg-primary-100 text-primary-700 font-semibold text-sm transition-colors'
                      : 'px-4 sm:px-5 py-1.5 sm:py-2 rounded-full border border-grayscale-300 bg-white text-grayscale-1200 font-medium text-sm hover:border-primary-400 hover:text-primary-700 transition-colors'
                  }
                >
                  {r.label}
                </button>
              );
            })}
          </div>
          <div className="mt-5">
            <ListingRequestBanner position="hero" compact />
          </div>
        </section>

        {/* ====================== CARD GRID ========================= */}
        <section className="pb-12 sm:pb-16 pt-2 sm:pt-4">
          {loadError ? (
            <p className="text-center text-sm text-grayscale-1100 py-12">
              Couldn&apos;t load the directory just now — please refresh in a moment.
            </p>
          ) : !data ? (
            <UniversitySkeletonGrid />
          ) : filtered.length === 0 ? (
            <p className="text-center text-sm text-grayscale-1100 py-12">
              No universities in this region yet — pick another to keep exploring.
            </p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
              {filtered.map((u, idx) => {
                const iso = (COUNTRY_ISO[u.country] || 'un').toUpperCase();
                const topCats = topCategoriesOf(u.name);
                // A card whose university has a public page links to it (the
                // live API sends page_path; the static snapshot does not).
                const href = u.page_path && u.page_path.startsWith('/') ? u.page_path : '';
                const CardTag: any = href ? 'a' : 'article';
                const linkProps = href ? {
                  href,
                  'aria-label': `${u.name} — ${u.technologies.toLocaleString()} technologies`,
                  onClick: (e: React.MouseEvent) => {
                    trackClick('unis_university_click', { code: u.code || null, position: idx + 1 });
                    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
                    e.preventDefault();
                    navigate(href);
                    window.scrollTo({ top: 0 });
                  },
                } : {};
                return (
                  <CardTag
                    key={u.code || u.name}
                    {...linkProps}
                    className={`rounded-2xl bg-primary-100/40 hover:bg-primary-100/60 transition-colors p-5 sm:p-6 flex flex-col items-center text-center${href ? ' no-underline text-inherit cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-600' : ''}`}
                  >
                    {/* Flag + ISO code stacked, matching the Canva header */}
                    <img
                      src={flagSrc(u.country)}
                      alt={u.country}
                      width={36}
                      height={27}
                      loading="lazy"
                      className="w-9 h-[27px] object-cover rounded-sm shadow-sm border border-grayscale-100"
                    />
                    <div className="mt-2 text-[11px] sm:text-xs font-bold tracking-wider text-grayscale-1100">
                      {iso}
                    </div>
                    <h3 className="mt-1 text-sm sm:text-base font-bold text-grayscale-1300 leading-snug">
                      {u.name}
                    </h3>
                    <p className="mt-1 text-xs sm:text-sm text-grayscale-1100">
                      {u.technologies > 0
                        ? `${u.technologies.toLocaleString()} technologies`
                        : 'Coming soon'}
                    </p>

                    {topCats.length > 0 && (
                      <div className="mt-3 flex flex-wrap items-center justify-center gap-1.5">
                        {topCats.map(c => (
                          <span
                            key={c}
                            title={c}
                            className="px-2.5 py-1 rounded-md bg-white text-[11px] sm:text-xs font-semibold text-grayscale-1300 border border-grayscale-200"
                          >
                            {shortenCategory(c)}
                          </span>
                        ))}
                      </div>
                    )}
                    {href && (
                      <span className="mt-3 text-xs font-semibold text-primary-700">View technologies →</span>
                    )}
                  </CardTag>
                );
              })}
            </div>
          )}
        </section>

        {/* ====================== GET LISTED ======================== */}
        <section className="pb-12 sm:pb-16">
          <ListingRequestBanner position="bottom" />
        </section>
      </main>

      <NewFooter
        onShowAboutUs={() => { trackClick('universities_footer_about_us'); window.location.href = '/about'; }}
        onShowPrivacy={() => { trackClick('universities_footer_privacy'); setShowPrivacyModal(true); }}
        onShowTerms={() => { trackClick('universities_footer_terms'); setShowTermsModal(true); }}
        onShowRdSymposium={() => { trackClick('universities_footer_rd_symposium'); window.location.href = '/rd-symposium'; }}
        onShowWhitePaper={() => { trackClick('universities_footer_whitepaper'); setShowWhitePaperModal(true); }}
        onShowUniversities={() => { trackClick('universities_footer_self'); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
        onShowComet={() => { trackClick('universities_footer_comet'); window.location.href = '/comet'; }}
      />

      {showPrivacyModal && (
        <PrivacyPolicyModal isOpen={showPrivacyModal} onClose={() => setShowPrivacyModal(false)} />
      )}
      {showTermsModal && (
        <TermsOfServiceModal isOpen={showTermsModal} onClose={() => setShowTermsModal(false)} />
      )}
      <WhitePaperModal
        isOpen={showWhitePaperModal}
        onClose={() => setShowWhitePaperModal(false)}
        formSource="universities_footer_whitepaper_modal"
      />

      {showLoginModal && (
        <LoginModal
          onClose={closeAllAuthModals}
          onLogin={onLogin}
          onSignUpClick={openSignUp}
        />
      )}
      {showSignUpModal && (
        <SignUpModal
          onClose={closeAllAuthModals}
          onSignupSuccess={handleSignUpSuccess}
        />
      )}
      {showSignupRequiredModal && (
        <SignupRequiredModal
          isOpen={showSignupRequiredModal}
          onClose={closeAllAuthModals}
          onLogin={openLogin}
          onSignup={openSignUp}
        />
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Skeleton — shown while /analytics_pie.json is in flight
// ---------------------------------------------------------------------------

const UniversitySkeletonGrid: React.FC = () => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
    {Array.from({ length: 8 }).map((_, i) => (
      <div
        key={i}
        className="rounded-2xl bg-primary-100/40 p-5 sm:p-6 flex flex-col items-center text-center animate-pulse"
      >
        <div className="w-9 h-[27px] bg-grayscale-200 rounded-sm" />
        <div className="mt-2 h-3 w-8 bg-grayscale-200 rounded" />
        <div className="mt-2 h-4 w-32 bg-grayscale-200 rounded" />
        <div className="mt-2 h-3 w-24 bg-grayscale-200 rounded" />
        <div className="mt-3 flex gap-1.5">
          <div className="h-6 w-16 bg-grayscale-200 rounded-md" />
          <div className="h-6 w-12 bg-grayscale-200 rounded-md" />
          <div className="h-6 w-14 bg-grayscale-200 rounded-md" />
        </div>
      </div>
    ))}
  </div>
);

export default UniversitiesPage;
